import { LogOut, Menu, Award, LayoutDashboard, BookOpen, Settings, Mail, Activity, BookOpenText, Share2, Bell, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { NotificationBadge } from "@/components/notifications/NotificationBadge";

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Pages that have "Back to Dashboard" button
  const pagesWithBackButton = [
    '/settings',
    '/testing-modules',
    '/getting-started',
    '/activities-and-goals',
    '/journal',
    '/notifications',
    '/contact-us'
  ];

  // Check if we're on a test-related route or child profile route
  const isTestRoute = location.pathname.includes('/test/');
  const isChildProfileRoute = location.pathname.includes('/child/');

  // Always show menu on dashboard and other specified routes
  const showMenu = location.pathname === '/dashboard' || 
                  pagesWithBackButton.includes(location.pathname) || 
                  isTestRoute || 
                  isChildProfileRoute || 
                  isMobile;

  const handleNavigation = (path: string) => {
    navigate(path);
    
    if (path === '/dashboard' && location.pathname === '/dashboard') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
      
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        console.error("Error during global signout:", error);
      }
      
      toast({
        title: "Logged out successfully",
        description: "You have been signed out of your account",
        duration: 2000,
      });
      
      navigate("/login");
    } catch (error) {
      console.error("Error in logout process:", error);
      navigate("/login");
    }
  };

  const navigationItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: GraduationCap, label: "Getting Started", path: "/getting-started" },
    { icon: BookOpen, label: "Testing Modules", path: "/testing-modules" },
    { icon: Activity, label: "Activities and Goals", path: "/activities-and-goals" },
    { icon: BookOpenText, label: "Journal", path: "/journal" },
    { icon: Settings, label: "Settings", path: "/settings" },
    { icon: Mail, label: "Contact Us", path: "/contact-us" },
    { icon: Share2, label: "Tell a Friend", onClick: () => {
      const shareData = {
        title: "Bright Stages",
        text: "Join me on Bright Stages - the platform that helps track and improve your child's developmental journey!",
        url: window.location.origin,
      };
      
      if (navigator.share) {
        navigator.share(shareData);
      } else {
        navigator.clipboard.writeText(shareData.url);
        toast({
          title: "Link copied!",
          description: "Share the link with your friends",
          duration: 2000,
        });
      }
    }},
  ];

  return (
    <header className="w-full bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Left section - Menu */}
          <div className="lg:w-1/4">
            {showMenu && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[400px]">
                  <div className="flex flex-col space-y-6">
                    <div className="flex flex-col items-center mb-6 pt-4">
                      <img 
                        src="/lovable-uploads/d62a4e07-f701-4335-84a0-c9be341a0477.png" 
                        alt="Bright Stages" 
                        className="h-20 mb-2 transition-all duration-300 hover:scale-105"
                        onClick={() => handleNavigation("/dashboard")}
                        loading="eager"
                        fetchPriority="high"
                      />
                      <Award className="h-8 w-8 text-primary animate-float mt-2" />
                    </div>
                    
                    <nav className="space-y-2">
                      {navigationItems.map((item) => (
                        <Button
                          key={item.label}
                          variant="ghost"
                          className="w-full justify-start gap-2"
                          onClick={item.onClick || (() => handleNavigation(item.path))}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.label}</span>
                        </Button>
                      ))}
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={handleLogout}
                      >
                        <LogOut className="h-5 w-5" />
                        <span>Sign Out</span>
                      </Button>
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>

          {/* Center section - Logo */}
          <div className="flex justify-center">
            {/* Desktop Logo */}
            <div 
              className="hidden lg:block cursor-pointer"
              onClick={() => handleNavigation("/dashboard")}
            >
              <img 
                src="/lovable-uploads/d62a4e07-f701-4335-84a0-c9be341a0477.png" 
                alt="Bright Stages" 
                className="h-24 transition-all duration-300 hover:scale-105"
                loading="eager"
                fetchPriority="high"
              />
            </div>

            {/* Mobile Logo */}
            <div 
              className="lg:hidden cursor-pointer"
              onClick={() => handleNavigation("/dashboard")}
            >
              <img 
                src="/lovable-uploads/d62a4e07-f701-4335-84a0-c9be341a0477.png" 
                alt="Bright Stages" 
                className="h-14 md:h-16 transition-all duration-300 hover:scale-105"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
          
          {/* Right section - Notifications & Logout */}
          <div className="flex items-center justify-end space-x-2 md:space-x-4 lg:w-1/4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => handleNavigation("/notifications")}
            >
              <Bell className="h-5 w-5" />
              <NotificationBadge />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              className="text-gray-600 hover:text-red-600"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
